import { Input, InputRef, Spin } from 'antd';
import Box from 'components/Box';
import Button from 'components/Button';
import { InputWithKeyboardStyled } from 'components/DollarAmount/Input';
import DollarAmountKeyboard from 'components/DollarAmount/Keyboard';
import { DollarAmountInputRef } from 'components/DollarAmount/helpers';
import Modal from 'components/Modal';
import CurrencyKeyBoard, { CurrencyKeyBoardRef } from 'components/NumberKeyBoard/Currency';
import NumberPadV2 from 'components/NumberKeyBoard/Currency/NumberPadV2';
import Text from 'components/Text';
import { IGiftCardV3PaymentValue } from 'features/cashier/services/types/giftCard';
import { IBillDetailData } from 'features/payment/services/types/bill';
import React, { useState } from 'react';
import shopSelectors from 'services/shop/selectors';
import styled from 'styled-components';
import qrScanIcon from '../QR-scan.svg';
import { IUseGetInfoGiftCardType } from '../useGetInfoGiftCard';
import GiftCardInfoBox from './GiftCardInfoBox';
import GiftCardTicketInfo from './GiftCardTicketInfo';
import ModalChooseNextPayment, { ModalChooseNextPaymentRef } from 'features/payment/pages/PaymentPage/CashierSide/components/GiftCard/ModalChooseNextPayment';
import { removeSpecificScanCode } from 'features/cashier/components/CenterMainBody/ExtendFeatures/GiftCard/v2/helper';
type Props = {
  visible?: boolean;
  onClose?: () => void;
  onRedeem: () => void;
  onPayment: (giftCards: IGiftCardV3PaymentValue[]) => void;
  currencyRef: React.RefObject<CurrencyKeyBoardRef>;
  dollarRef: React.RefObject<DollarAmountInputRef>;
  amount: number | null;
  setAmount: (value: React.SetStateAction<number | null>) => void
  isValid?: boolean;
  giftCard: IUseGetInfoGiftCardType;
  inputCodeRef: React.RefObject<InputRef>;
  onScan: () => void;
  giftCardBalance: number;
  detail?: IBillDetailData | null;
  giftCardPayments?: IGiftCardV3PaymentValue[];
  onDeleteItem?: (o: IGiftCardV3PaymentValue) => void;
  remaining?: number;
  modalChooseNextPaymentRef?: React.RefObject<ModalChooseNextPaymentRef>;
  amountMsg?: string;
};
const ModalGiftCardPaymentV2 = ({
  onRedeem, remaining, amountMsg,
  onDeleteItem, giftCardPayments = [], giftCardBalance, onScan, inputCodeRef, giftCard, isValid,
  onPayment, setAmount, amount = 0, dollarRef, currencyRef, visible, onClose = () => undefined, detail,
  modalChooseNextPaymentRef
}: Props) => {
  const allSetting = shopSelectors.data.allSetting();
  const [isNumberCard, setIsNumberCard] = useState(false);
  const handleChangeGiftCardCode = (code: string) => {
    giftCard.setCode(code);
    setIsNumberCard(true);
  };

  const onKBFocus = (type: 'currency' | 'numberCard') => () => setIsNumberCard(type === 'numberCard');

  return (
    <Modal
      visible={visible}
      onClose={onClose}
      width={'auto'} footer={null} className='modal-overflow-unset modal-non-opacity'
      hiddenHeader
      noneBodyStyle
      okTitle='Redeem'
    >
      <ContainerModal>
        <ContainerKeyboard>
          <HeaderModal>
            <div className="modal-title">gift cards</div>
          </HeaderModal>
          <Spin spinning={giftCard.loading}>
            <GiftCardNumberContainer>
              <Text className='modal-label' mb={0.5}>
                Gift Card Number:
              </Text>
              <div className="input-selector-container">
                <Input
                  ref={inputCodeRef}
                  className='gift-card-number-input' size='small' value={giftCard.code} onChange={e => giftCard.setCode(removeSpecificScanCode(e.target.value))}
                  onFocus={onKBFocus('numberCard')}
                  onKeyPress={(event) => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                />
                <button type='button' className='btn-scan' onClick={onScan}><img src={qrScanIcon} alt="qrScanIcon" className='qr-scan-icon' /></button>
              </div>
              {giftCard.msg && <div className="modal-warning">{giftCard.msg}</div>}
            </GiftCardNumberContainer>
          </Spin>

          {!allSetting?.activeFormatMoney ? <>
            <CurrencyKeyBoard v2 onFocus={onKBFocus('currency')} isHiddenKB={isNumberCard} ref={currencyRef} value={amount} onChange={val => setAmount(val || 0)} />
            {amountMsg && <AmountWarning>{amountMsg}</AmountWarning>}
            <div hidden={!isNumberCard}>
              <NumberPadV2
                type={'NUMBER'}
                value={giftCard.code}
                controlRef={inputCodeRef}
                onChange={handleChangeGiftCardCode}
              />
            </div>
          </> : <>
            <Box display='flex' flexDirection='column' gap='2'>
              <Text className='modal-keyboard-label' mt={1} mb={-0.5}>
                Enter Amount:
              </Text>
              <InputWithKeyboardStyled
                ref={dollarRef}
                value={amount}
                onChange={setAmount}
                inputMode='none'
                onFocus={onKBFocus('currency')}
              />
              {amountMsg && <AmountWarning>{amountMsg}</AmountWarning>}
              <GiftCardInfoBox
                data={giftCard.data}
                balance={giftCardBalance > 0 ? giftCardBalance : 0}
                resultGiftCard={giftCard.resultGiftCard}
              />
              <div hidden={!isNumberCard}>
                <NumberPadV2
                  type={'NUMBER'}
                  value={giftCard.code}
                  controlRef={inputCodeRef}
                  onChange={handleChangeGiftCardCode}
                />
              </div>
              <div hidden={isNumberCard}>
                <DollarAmountKeyboard
                  controlRef={dollarRef}
                  value={amount}
                  onChange={setAmount}
                />
              </div>
            </Box>
          </>}
          <FooterActions>
            <Button height={4.375} cardCashier onClick={onClose} htmlType='button'><span>Close</span></Button>
            <Button
              height={4.375} cardCashier htmlType='button' ntype='PRIMARY'
              // Without a loaded card, Redeem continues with the gift cards already added, so only block an invalid amount on a loaded card.
              disabled={!!remaining && !!giftCard.data && !isValid}
              onClick={!remaining ? () => onPayment(giftCardPayments) : onRedeem}
            >
              <span>{!remaining ? 'Ok' : 'Redeem'}</span>
            </Button>
          </FooterActions>
        </ContainerKeyboard>
        <GiftCardTicketInfo
          detail={detail}
          remaining={remaining}
          giftCardPayments={giftCardPayments}
          onDeleteItem={onDeleteItem}
        />
      </ContainerModal>
      <ModalChooseNextPayment ref={modalChooseNextPaymentRef} />
    </Modal >
  );
};

export default ModalGiftCardPaymentV2;
const ContainerModal = styled.div`
  display: flex;
`;
const HeaderModal = styled.div`
 border-radius: 5px 5px 0px 0px;
  background: #232F3E;
  padding: 12px 0;
  height: auto;
  .modal-title {
    color: #FFF;
    background: #232F3E;
    text-align: center;
    font-family: Poppins;
    font-size: 30px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    text-transform: uppercase;
  }
`;
const GiftCardNumberContainer = styled.div`
margin-bottom: 1rem;
margin-top: 1rem;
display: flex;
flex-direction: column;  
.modal-label {
  color: #64748B;
  text-align: center;
  font-family: Poppins;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}
.modal-warning {
  color: #ff7d00;
  text-align: center;
  font-family: Poppins;
  align-self: stretch;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}
.input-selector-container {
  display: flex;
  align-items: center;
  position: relative;
  height: 64px;
  margin: 0 40px;
  
  .qr-scan-icon {
    width: 64px;
    height: 64px;
  }

  .btn-scan {
    display: flex;
    width: 80px;
    height: 80px;
    padding: 8px;
    justify-content: center;
    align-items: center;
    border-radius: 0px 4px 4px 0px;
    background: #FFF1F6;
    position: absolute;
    right: 0;
  }
}
.gift-card-number-input {
  color: #1D2129;
  text-align: center;
  font-family: Poppins;
  align-self: stretch;
  font-size: 30px;
  height: unset;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  border: none;
  outline: none;
  box-shadow: none;
}
`;
const AmountWarning = styled.div`
  color: #ff7d00;
  text-align: center;
  font-family: Poppins;
  font-size: 22.4px;
  font-weight: 400;
  padding: 0 16px;
`;
const FooterActions = styled.div`
  display: flex;
  padding: 16px !important;
  align-items: center;
  gap: 16px;
  button {
    flex: 1;
    span {
      color: #1D2129;
      text-align: center;
      font-family: Poppins;
      font-size: 22px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
    }
  }
`;
const ContainerKeyboard = styled.div`
  width: 980px;

`;
